import {
	ChangeDetectionStrategy,
	Component,
	ElementRef,
	input,
	InputSignal,
	OnChanges,
	OnDestroy,
	signal,
	SimpleChanges,
	Signal,
	viewChild,
	WritableSignal,
	inject,
} from '@angular/core'
import { PriceTargetInterface } from '@modules/twelve-data-module/interfaces/price-target.interface'
import { LanguageEnum } from '@modules/lang-module/enums/language.enum'
import { ThemeEnum } from '@modules/theme-module/enums/theme.enum'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { UseLocales } from '@modules/lang-module/decorators/use-locales'
import ru from './core/locale/ru.json'
import en from './core/locale/en.json'
import { PricePipe } from '@shared/pipes/price.pipe'
import { ProfitPipe } from '@shared/pipes/profit.pipe'
import { HighlightDirective } from '@shared/directives/higlight.directive'
import {
	AutoscaleInfo,
	createChart,
	IChartApi,
	IPriceLine,
	ISeriesApi,
	IPrimitivePaneRenderer,
	IPrimitivePaneView,
	LineData,
	LineSeries,
	LineStyle,
	SeriesAttachedParameter,
	SeriesDefinition,
	WhitespaceData,
} from 'lightweight-charts'
import { CanvasRenderingTarget2D } from 'fancy-canvas'
import { StocksService } from '@modules/stocks-module/general/services/stocks.service'
import { TradingViewTimeframesEnum } from '@modules/trading-view-module/enums/trading-view-timeframes.enum'
import { TradingViewHelperService } from '@modules/trading-view-module/services/trading-view-helper.service'
import { finalize, Subject, takeUntil } from 'rxjs'
import { LatestPricesResponseInterface } from '@modules/stocks-module/general/interfaces/latest-prices-response.interface'
import { LatestPricesInterface } from '@modules/stocks-module/general/interfaces/latest-prices.interface'
import { TuiSkeleton } from '@taiga-ui/kit'
import { WidgetInterface } from '@modules/widgets-module/interfaces/widget.interface'
import { PriceZoneInterface } from './core/interfaces/price-zone.interface'
import { UtilsService } from '@shared/services/utils.service'
import { ColorCSSVarEnum } from '@shared/enums/color-css-var.enum'
import { RecommendationsInterface } from '@modules/twelve-data-module/interfaces/recommendations.interface'
import { LiveTickerInterface } from '@modules/stocks-module/live-ticker/interfaces/live-ticker.interface'

@UseLocales({ ru, en })
@Component({
	selector: 'app-price-forecast',
	templateUrl: './price-forecast.component.html',
	styleUrl: './price-forecast.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [TranslatePipe, PricePipe, ProfitPipe, HighlightDirective, TuiSkeleton],
})
export class PriceForecastComponent implements OnChanges, OnDestroy {
	protected readonly languageEnum: typeof LanguageEnum = LanguageEnum
	protected readonly themeEnum: typeof ThemeEnum = ThemeEnum
	protected readonly date: DateConstructor = Date
	protected readonly math: Math = Math
	protected readonly lineStyle: typeof LineStyle = LineStyle
	protected readonly lineSeries: SeriesDefinition<'Line'> = LineSeries
	protected readonly colorCSSVarEnum: typeof ColorCSSVarEnum = ColorCSSVarEnum
	protected readonly tradingViewTimeframesEnum: typeof TradingViewTimeframesEnum = TradingViewTimeframesEnum

	public widget: InputSignal<WidgetInterface | null> = input<WidgetInterface | null>(null)
	public lang: InputSignal<LanguageEnum> = input<LanguageEnum>(this.languageEnum.ENGLISH)
	public theme: InputSignal<ThemeEnum> = input<ThemeEnum>(this.themeEnum.LIGHT)
	public priceTarget: InputSignal<PriceTargetInterface | null> = input<PriceTargetInterface | null>(null)
	public isLoadingPriceTarget: InputSignal<boolean> = input<boolean>(false)
	public recommendations: InputSignal<RecommendationsInterface | null> = input<RecommendationsInterface | null>(null)
	public isLoadingRecommendations: InputSignal<boolean> = input<boolean>(false)
	public liveTicker: InputSignal<LiveTickerInterface | null> = input<LiveTickerInterface | null>(null)
	public totalAnalysts: InputSignal<number> = input<number>(0)

	private _chartEl: Signal<ElementRef<HTMLDivElement> | undefined> = viewChild<ElementRef<HTMLDivElement>>('chart')

	private _destroy$: Subject<void> = new Subject<void>()

	private readonly _stocksService: StocksService = inject<StocksService>(StocksService)
	private readonly _tradingViewHelperService: TradingViewHelperService =
		inject<TradingViewHelperService>(TradingViewHelperService)
	private readonly _translateService: TranslateService = inject<TranslateService>(TranslateService)
	private readonly _utilsService: UtilsService = inject<UtilsService>(UtilsService)

	private _bars: (LineData | WhitespaceData)[] = []
	private _chart: IChartApi | null = null
	private _series: ISeriesApi<'Line'> | null = null

	private _highPriceLine: IPriceLine | null = null
	private _lowPriceLine: IPriceLine | null = null
	private _avgPriceLine: IPriceLine | null = null

	private _zoneGreen: PriceZoneInterface | null = null
	private _zoneRed: PriceZoneInterface | null = null

	protected isLoadingBars: WritableSignal<boolean> = signal<boolean>(false)
	private _createZonePrimitive(position: 'above' | 'below', color: string): PriceZoneInterface {
		let param: SeriesAttachedParameter | null = null
		let leftX: number = 0
		let currentColor: string = color

		const paneView: IPrimitivePaneView = {
			zOrder: (): 'bottom' => 'bottom',
			renderer: (): IPrimitivePaneRenderer => ({
				draw: (target: CanvasRenderingTarget2D): void => {
					if (!param) return
					target.useBitmapCoordinateSpace((scope): void => {
						const context: CanvasRenderingContext2D = scope.context
						const verticalPixelRatio: number = scope.verticalPixelRatio
						const chartHeight: number = param?.chart.paneSize().height ?? 0
						const avgPrice: number = this.priceTarget()?.price_target.average ?? 0
						const middleY: number = param?.series.priceToCoordinate(avgPrice) ?? 0
						const topY: number = position === 'above' ? 0 : middleY
						const bottomY: number = position === 'above' ? middleY : chartHeight
						const left: number = this.math.max(0, leftX) * scope.horizontalPixelRatio
						const width: number = scope.bitmapSize.width - left
						if (width <= 0) return
						const top: number = topY * verticalPixelRatio
						const height: number = bottomY * verticalPixelRatio - top
						if (height <= 0) return
						context.fillStyle = currentColor
						context.fillRect(left, top, width, height)
					})
				},
			}),
		}

		return {
			attached: (seriesAttachedParameter: SeriesAttachedParameter): void => {
				param = seriesAttachedParameter
			},
			detached: (): void => {
				param = null
			},
			paneViews: (): IPrimitivePaneView[] => [paneView],
			updateAllViews: (): void => {},

			applyOptions: (color: string, leftHorizontal: number): void => {
				currentColor = color
				leftX = leftHorizontal
				param?.requestUpdate()
			},
		}
	}

	private _dropChartEl(): void {
		const chartEl: ElementRef<HTMLDivElement> | undefined = this._chartEl()
		if (!chartEl) return
		this._series = null
		this._zoneGreen = null
		this._zoneRed = null
		chartEl.nativeElement.innerHTML = ''
	}

	private _getBars(): void {
		const symbol: string | undefined = this.priceTarget()?.meta.symbol
		if (!symbol) return

		const nowDate: Date = new this.date()
		const today: number = nowDate.getTime()

		const yearAgoDate: Date = new this.date(nowDate)
		yearAgoDate.setFullYear(yearAgoDate.getFullYear() - 1)
		const yearAgo: number = yearAgoDate.getTime()

		const urlBars: string = this._tradingViewHelperService.generateUrlBars(
			this.tradingViewTimeframesEnum.oneDay,
			yearAgo,
			today,
		)

		this.isLoadingBars.set(true)
		this._stocksService
			.getTickerHistory(symbol, urlBars)
			.pipe(
				takeUntil(this._destroy$),
				finalize((): void => this.isLoadingBars.set(false)),
			)
			.subscribe((response: LatestPricesResponseInterface): void => {
				this._bars = response.results
					.map((item: LatestPricesInterface): LineData | WhitespaceData => {
						return {
							time: new this.date(item.t).toISOString().slice(0, 10),
							value: item.c,
						}
					})
					.reverse()

				this._createChart()
			})
	}

	private _getCoordsLastBar(): number {
		if (!this._chart || !this._bars.length) return 0
		const lastBar: LineData | WhitespaceData | undefined = this._bars[this._bars.length - 1]
		if (!lastBar) return 0
		return this._chart.timeScale().timeToCoordinate(lastBar.time) ?? 0
	}

	private _updateZones(): void {
		const priceTarget: PriceTargetInterface | null = this.priceTarget()
		if (!priceTarget || !this._zoneGreen || !this._zoneRed) return

		const leftX: number = this._getCoordsLastBar()
		const greenColor: string = this._utilsService.getColorByCSSVar(this.colorCSSVarEnum.SUCCESS_100)
		const redColor: string = this._utilsService.getColorByCSSVar(this.colorCSSVarEnum.ERROR_100)

		this._zoneGreen.applyOptions(greenColor, leftX)
		this._zoneRed.applyOptions(redColor, leftX)
	}

	private _createChart(): void {
		const chartEl: ElementRef<HTMLDivElement> | undefined = this._chartEl()
		if (!chartEl || !this._bars.length) return
		this._dropChartEl()

		this._chart = createChart(chartEl.nativeElement, {
			height: 200,
			autoSize: true,
			layout: {
				textColor: this._utilsService.getColorByCSSVar(this.colorCSSVarEnum.TEXT_400),
				background: { color: 'transparent' },
			},
			grid: {
				horzLines: {
					visible: false,
				},
				vertLines: {
					visible: false,
				},
			},
			rightPriceScale: {
				borderColor: this._utilsService.getColorByCSSVar(this.colorCSSVarEnum.LINE_400),
			},
			timeScale: {
				borderColor: this._utilsService.getColorByCSSVar(this.colorCSSVarEnum.LINE_400),
				rightOffsetPixels: 100,
			},
			localization: {
				locale: this.lang(),
			},
			handleScroll: false,
			handleScale: false,
		})

		this._chart.timeScale().subscribeVisibleTimeRangeChange((): void => this._updateZones())
		this._createSeries()
		this._chart.timeScale().fitContent()
	}

	private _createSeries(): void {
		if (!this._chart) return

		this._series = this._chart.addSeries(this.lineSeries, {
			color: this._utilsService.getColorByCSSVar(this.colorCSSVarEnum.INFO_300),
			autoscaleInfoProvider: (original: () => AutoscaleInfo | null): AutoscaleInfo | null => {
				const base: AutoscaleInfo | null = original()
				if (!base?.priceRange) return null

				const priceTarget: PriceTargetInterface | null = this.priceTarget()
				if (!priceTarget) return null

				return {
					priceRange: {
						minValue: this.math.min(base.priceRange.minValue, priceTarget.price_target.low),
						maxValue: this.math.max(base.priceRange.maxValue, priceTarget.price_target.high),
					},
				}
			},
		})
		this._series.setData(this._bars)
		this._drawLines(this._series)
		this._attachZones(this._series)
	}

	private _drawLines(series: ISeriesApi<'Line'>): void {
		const priceTarget: PriceTargetInterface | null = this.priceTarget()
		if (!priceTarget) return

		this._highPriceLine = series.createPriceLine({
			price: priceTarget.price_target.high,
			color: this._utilsService.getColorByCSSVar(this.colorCSSVarEnum.SUCCESS_300),
			lineWidth: 1,
			lineStyle: this.lineStyle.Solid,
			title: this._translateService.instant('priceForecast.maxLine'),
		})

		this._lowPriceLine = series.createPriceLine({
			price: priceTarget.price_target.low,
			color: this._utilsService.getColorByCSSVar(this.colorCSSVarEnum.ERROR_300),
			lineWidth: 1,
			lineStyle: this.lineStyle.Solid,
			title: this._translateService.instant('priceForecast.minLine'),
		})

		this._avgPriceLine = series.createPriceLine({
			price: priceTarget.price_target.average,
			color: this._utilsService.getColorByCSSVar(this.colorCSSVarEnum.TEXT_400),
			lineWidth: 1,
			lineStyle: this.lineStyle.Solid,
			title: this._translateService.instant('priceForecast.avgLine'),
		})
	}

	private _attachZones(series: ISeriesApi<'Line'>): void {
		const priceTarget: PriceTargetInterface | null = this.priceTarget()
		if (!priceTarget) return

		const greenColor: string = this._utilsService.getColorByCSSVar(this.colorCSSVarEnum.SUCCESS_100)
		const redColor: string = this._utilsService.getColorByCSSVar(this.colorCSSVarEnum.ERROR_100)

		this._zoneGreen = this._createZonePrimitive('above', greenColor)
		this._zoneRed = this._createZonePrimitive('below', redColor)

		series.attachPrimitive(this._zoneGreen)
		series.attachPrimitive(this._zoneRed)
	}

	private _updateChart(): void {
		if (!this._chart) return

		this._chart.applyOptions({
			localization: {
				locale: this.lang(),
			},
			layout: {
				textColor: this._utilsService.getColorByCSSVar(this.colorCSSVarEnum.TEXT_400),
				background: { color: 'transparent' },
			},
		})
	}

	private _updateLines(): void {
		this._highPriceLine?.applyOptions({
			title: this._translateService.instant('priceForecast.maxLine'),
			color: this._utilsService.getColorByCSSVar(this.colorCSSVarEnum.SUCCESS_300),
		})
		this._lowPriceLine?.applyOptions({
			title: this._translateService.instant('priceForecast.minLine'),
			color: this._utilsService.getColorByCSSVar(this.colorCSSVarEnum.ERROR_300),
		})
		this._avgPriceLine?.applyOptions({
			title: this._translateService.instant('priceForecast.avgLine'),
			color: this._utilsService.getColorByCSSVar(this.colorCSSVarEnum.TEXT_400),
		})

		this._updateZones()
	}

	protected getPercent(price: number | undefined): number {
		const currentPrice: number | undefined = this.priceTarget()?.price_target?.current
		if (!price || !currentPrice) return 0
		return +(((price - currentPrice) / currentPrice) * 100).toFixed(2)
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (this.priceTarget()) {
			if (changes['lang'] || changes['theme']) {
				this._updateChart()
				this._updateLines()
			}
			if (changes['priceTarget']) {
				this._getBars()
			}
		}

		if (changes['widget']) {
			this._chart?.timeScale().fitContent()
		}
	}

	ngOnDestroy(): void {
		this._dropChartEl()
		this._destroy$.next()
		this._destroy$.complete()
	}
}
